import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, formatINR } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import InstagramSection from "@/components/InstagramSection";
import { ChevronLeft, ChevronRight } from "lucide-react";

const HERO_SLIDES = [
  {
    img: "https://images.unsplash.com/photo-1663811397207-418a92396ad5?w=2000&q=85",
    overline: "Winter Editions",
    title: "Direct from Factory. Crafted for You.",
    sub: "Furniture made to your dimensions, in your finish, without the retail markup.",
    cta: { label: "Explore Sofas", to: "/category/sofas-seating" },
  },
  {
    img: "https://images.pexels.com/photos/13722886/pexels-photo-13722886.jpeg",
    overline: "Bedroom Series",
    title: "A quieter kind of luxury.",
    sub: "Low-profile beds in solid teak, oak & walnut.",
    cta: { label: "Explore Beds", to: "/category/beds" },
  },
  {
    img: "https://images.unsplash.com/photo-1648877075670-f4f6ec3fb65b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtaW5pbWFsaXN0JTIwd29vZGVuJTIwYmVkJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg1MzU0NDUxfDA&ixlib=rb-4.1.0&q=85",
    overline: "Bespoke",
    title: "Order swatches. Then commit.",
    sub: "₹299 fabric & wood samples — feel it before you buy the bed.",
    cta: { label: "Order Swatches", to: "/category/swatches" },
  },
];

const CATEGORY_TILES = [
  { slug: "beds", name: "Beds", img: "https://images.unsplash.com/photo-1517912191359-67659f8690a2?w=1200&q=85" },
  { slug: "sofas-seating", name: "Sofas & Seating", img: "https://images.unsplash.com/photo-1651879253285-2c9e621e67a2?w=1200&q=85" },
  { slug: "dining-sets", name: "Dining Sets", img: "https://images.unsplash.com/photo-1617806118233-18e1de247200?w=1200&q=85" },
  { slug: "wardrobes-storage", name: "Wardrobes", img: "https://images.unsplash.com/photo-1595428774223-ef52624120d2?w=1200&q=85" },
];

function ShopTheLook({ scenes, productsBySlug }) {
  const [idx, setIdx] = useState(0);
  if (!scenes.length) return null;
  const s = scenes[idx];
  const products = (s.product_slugs || []).map((slug) => productsBySlug[slug]).filter(Boolean);

  return (
    <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24" data-testid="home-shop-the-look">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="font-micro text-[#B8895C] mb-3">Shop the Look</div>
          <h2 className="font-serif text-[#EDE4D3] text-4xl md:text-5xl">Scenes, curated.</h2>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIdx((idx - 1 + scenes.length) % scenes.length)}
            className="w-10 h-10 flex items-center justify-center text-[#EDE4D3] hover:text-[#B8895C] transition-colors"
            style={{ border: "1px solid var(--hairline)" }}
            data-testid="stl-prev"
          >
            <ChevronLeft size={18} strokeWidth={1} />
          </button>
          <div className="font-micro text-[#8A8680]" data-testid="stl-counter">{idx + 1} / {scenes.length}</div>
          <button
            onClick={() => setIdx((idx + 1) % scenes.length)}
            className="w-10 h-10 flex items-center justify-center text-[#EDE4D3] hover:text-[#B8895C] transition-colors"
            style={{ border: "1px solid var(--hairline)" }}
            data-testid="stl-next"
          >
            <ChevronRight size={18} strokeWidth={1} />
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-12 items-start">
        <div className="img-cinematic aspect-[4/5] fade-in" key={idx} style={{ background: "#141414" }} data-testid={`stl-scene-${idx}`}>
          <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
        </div>
        <div className="fade-in" key={`meta-${idx}`}>
          <h3 className="font-serif text-[#EDE4D3] text-3xl md:text-4xl leading-tight mb-4" data-testid="stl-title">{s.title}</h3>
          {s.subtitle && <p className="text-[#8A8680] leading-relaxed mb-10 max-w-md">{s.subtitle}</p>}
          <div className="grid grid-cols-2 gap-4" data-testid="stl-thumbnails">
            {products.map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.slug}`}
                className="group block"
                data-testid={`stl-thumb-${p.slug}`}
              >
                <div className="img-cinematic aspect-square" style={{ background: "#0B0B0C" }}>
                  {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                </div>
                <div className="mt-3 font-serif text-[#EDE4D3] text-sm group-hover:text-[#B8895C] transition-colors duration-500 leading-tight">{p.name}</div>
                <div className="font-micro text-[#8A8680] mt-1">{formatINR(p.starts_at)}</div>
              </Link>
            ))}
          </div>
          <div className="mt-10 flex gap-2" data-testid="stl-dots">
            {scenes.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className="h-[1px] transition-all duration-700"
                style={{ width: i === idx ? 60 : 24, background: i === idx ? "#B8895C" : "#8A8680" }}
                data-testid={`stl-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [slide, setSlide] = useState(0);
  const [featured, setFeatured] = useState([]);
  const [freshFinds, setFreshFinds] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products?featured=true&limit=8`).then((r) => setFeatured(r.data)).catch(() => {});
    axios.get(`${API}/products?new_arrival=true&sort=newest&limit=10`).then((r) => setFreshFinds(r.data)).catch(() => {});
    axios.get(`${API}/shop-the-look-scenes`).then((r) => setScenes(r.data)).catch(() => {});
    axios.get(`${API}/products?limit=200`).then((r) => setAllProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 7000);
    return () => clearInterval(t);
  }, []);

  const productsBySlug = Object.fromEntries(allProducts.map((p) => [p.slug, p]));
  const s = HERO_SLIDES[slide];

  return (
    <div className="fade-in" data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[92vh] w-full overflow-hidden" data-testid="home-hero">
        {HERO_SLIDES.map((slideItem, i) => (
          <div key={i} className="absolute inset-0 transition-opacity duration-[1200ms]" style={{ opacity: i === slide ? 1 : 0 }}>
            <img src={slideItem.img} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(11,11,12,0.85), rgba(11,11,12,0.15) 60%, rgba(11,11,12,0.35))" }} />
          </div>
        ))}
        <div className="relative z-10 h-full max-w-[1440px] mx-auto px-6 md:px-16 flex flex-col justify-end pb-24">
          <div className="max-w-2xl">
            <div className="font-micro text-[#B8895C] mb-6">{s.overline}</div>
            <h1 className="font-serif text-[#EDE4D3] text-5xl md:text-7xl leading-[1.05] mb-6">{s.title}</h1>
            <p className="text-[#D8D6D1] max-w-lg text-base md:text-lg mb-10 leading-relaxed">{s.sub}</p>
            <Link to={s.cta.to} className="btn-bronze" data-testid="hero-cta">{s.cta.label}</Link>
          </div>
          <div className="mt-14 flex gap-3">
            {HERO_SLIDES.map((_, i) => (
              <button key={i} onClick={() => setSlide(i)} data-testid={`hero-dot-${i}`}
                className="h-[1px] transition-all duration-700"
                style={{ width: i === slide ? 60 : 24, background: i === slide ? "#B8895C" : "#8A8680" }} />
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORY TILES */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24" data-testid="home-categories">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="font-micro text-[#8A8680] mb-3">The Collections</div>
            <h2 className="font-serif text-[#EDE4D3] text-4xl md:text-5xl">Rooms, considered.</h2>
          </div>
          <Link to="/category/beds" className="hidden md:inline-block font-micro link-underline text-[#EDE4D3]">Browse All →</Link>
        </div>
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {CATEGORY_TILES.map((c) => (
            <Link key={c.slug} to={`/category/${c.slug}`}
              className="img-cinematic block relative aspect-[4/5]"
              style={{ background: "#141414" }}
              data-testid={`category-tile-${c.slug}`}>
              <img src={c.img} alt={c.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/25 flex items-end p-10">
                <div>
                  <div className="font-micro text-[#B8895C] mb-2">Explore</div>
                  <div className="font-serif text-[#EDE4D3] text-4xl">{c.name}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-8" style={{ borderTop: "1px solid var(--hairline)", borderBottom: "1px solid var(--hairline)" }}>
        <div className="marquee">
          <div className="marquee-inner font-serif text-[#B8895C] text-2xl md:text-3xl">
            {"Direct from Factory · Made to Order · Solid Teak · Bespoke · Handcrafted in Pitampura, Delhi · ".repeat(6)}
          </div>
        </div>
      </section>

      {/* FRESH FINDS */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24" data-testid="home-fresh-finds">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-micro text-[#8A8680] mb-3">Fresh Finds</div>
            <h2 className="font-serif text-[#EDE4D3] text-4xl md:text-5xl">New this season.</h2>
          </div>
        </div>
        <div className="overflow-x-auto -mx-6 md:mx-0 pb-4">
          <div className="flex gap-8 px-6 md:px-0">
            {freshFinds.map((p) => (
              <div key={p.id} className="w-72 shrink-0">
                <ProductCard p={p} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOP THE LOOK — multi-scene carousel with product thumbnails */}
      <ShopTheLook scenes={scenes} productsBySlug={productsBySlug} />

      {/* INSTAGRAM */}
      <InstagramSection />

      {/* FEATURED */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24" data-testid="home-featured">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-micro text-[#8A8680] mb-3">Studio Picks</div>
            <h2 className="font-serif text-[#EDE4D3] text-4xl md:text-5xl">The current selection.</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {featured.slice(0, 8).map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </section>

      {/* BANNER */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="p-16 flex flex-col justify-between" style={{ background: "#141414", border: "1px solid var(--hairline)" }}>
            <div>
              <div className="font-micro text-[#B8895C] mb-4">Bespoke</div>
              <h3 className="font-serif text-[#EDE4D3] text-4xl leading-tight mb-4">Customise a Piece.</h3>
              <p className="text-[#8A8680] leading-relaxed">Sketch. Image. Idea. We&apos;ll match materials and make it.</p>
            </div>
            <Link to="/custom-design" className="btn-ghost self-start mt-8" data-testid="banner-custom-design">Start a Commission</Link>
          </div>
          <div className="p-16 flex flex-col justify-between" style={{ background: "#B8895C", color: "#0B0B0C" }}>
            <div>
              <div className="font-micro mb-4">Direct from Factory</div>
              <h3 className="font-serif text-4xl leading-tight mb-4">Skip the middleman.</h3>
              <p className="leading-relaxed opacity-80">Our furniture ships direct from our Pitampura, Delhi atelier &mdash; no showrooms, no markup.</p>
            </div>
            <a href="https://wa.me/917982295008" target="_blank" rel="noreferrer" className="mt-8 self-start px-8 py-3 font-micro" style={{ border: "1px solid #0B0B0C" }} data-testid="banner-whatsapp">
              Chat on WhatsApp →
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
